import { useEffect, useMemo, useState } from "react";
import { tokensService } from "services/tokensService";
import { TokenElement } from "./components/Token";
import searchIcon from "../../../assets/svg/searchIcon.svg";
import Card from "components/card";
import { AddTokenForm } from "./components/AddTokenForm";
import Widget from "components/widget/Widget";
import { MdAdd } from "react-icons/md";
import { useAppStore } from "store/appStore";
import { AdminRole } from "types/auth";
import { useTokensStore } from "store/tokensStore";
import { useDebounce } from "hooks/useDebounce";
import { Button } from "components/Button";
import { ArrowLeftIcon } from "components/SvgComponents/ArrowLeft";
import { scrollToTop } from "utils/scrollToTop";
import { Loader } from "components/Loader";
import InfiniteScroll from "react-infinite-scroll-component";
import { Box, Input } from "@mui/material";
import { useNetworksStore } from 'store/networksStore';

export const Tokens = () => {
  const { admin } = useAppStore();	
  const { currentNetwork } = useNetworksStore();
  const [searchValue, setSearchValue] = useState("");
  const { tokens, setTokens, setTokensMeta, tokensMeta } = useTokensStore();
  const [haseMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const debounsedSearchValue = useDebounce(searchValue);
  const [isLoading, setIsLoading] = useState(false);

  const getTokens = async ({
    page,
    searchValue,
    isSetNewTokens,
    networkId,
  }: {
    page?: number;
    searchValue: string;
    isSetNewTokens?: boolean;
    networkId?: string;
  }) => {
    if (!networkId) return;
    try {
      const res = await tokensService.getTokens({
        page: page ? page + 1 : 1,
        search: searchValue,
        networkId,
      });

      res.data &&
        setTokens(isSetNewTokens ? res.data : [...tokens, ...res.data]);
      if (res.meta) {
        setTokensMeta(res.meta);
        setPage(res.meta.currentPage);
        setHasMore(res.meta.currentPage !== res.meta.totalPages);
      }
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    getTokens({
      searchValue: debounsedSearchValue,
      isSetNewTokens: true,
      networkId: currentNetwork?.id,
    });
  }, [debounsedSearchValue, currentNetwork]);

  const isAdmin = useMemo(() => {
    return admin?.role === AdminRole.SUPER_ADMIN;
  }, [admin]);

  return (
    <div className="mt-3">
      <div className="mb-3 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-3 3xl:grid-cols-3">
        <AddTokenForm
          onSuccess={() =>
            getTokens({ page: 1, searchValue, isSetNewTokens: true })
          }
        >
          <button className="rounded-2xl outline-none">
            <Widget
              icon={<MdAdd className="h-7 w-7" />}
              title={""}
              subtitle={"Add new token"}
            />
          </button>
        </AddTokenForm>
      </div>

      <Card>
        <div className="relative h-full">
          <div className="flex gap-4 px-8 py-4">
            {tokensMeta?.totalItems && (
              <span>{`Total tokens: ${tokensMeta?.totalItems}`}</span>
            )}
          </div>

          <Box
            sx={{
              mb: "20px",
            }}
          >
            <div className="relative mx-8 w-auto">
              <img
                width={24}
                height={24}
                style={{
                  zIndex: 5,
                  top: "50%",
                  transform: "translateY(-50%)",
                  position: "absolute",
                  left: "24px",
                }}
                src={searchIcon}
                alt="search Icon"
              />
              <Input
                autoFocus
                placeholder="Search by token"
                sx={{
                  ":before": {
                    display: "none",
                  },
                  ":after": {
                    display: "none",
                  },
                  minWidth: "254px",
                  height: "48px",
                  paddingLeft: "56px",
                  paddingRight: "28px",
                  bgcolor: "#F1F2F5",
                  borderRadius: "8px",
                  fontSize: "16px",
                  fontWeight: 400,
                  color: "#fff",
                  border: "1px solid transparent",
                  width: "100%",
                  backgroundColor: "#F0F4FF",
                }}
                onChange={(e) => setSearchValue(e.target.value)}
                value={searchValue}
              />
            </div>
          </Box>

          <div
            className="scrollbar-none sm:scrollbar-thin scrollbar-thumb-[#5663F2] scrollbar-track-[#F0F4FF] scrollbar-thumb-rounded-lg mb-8 mr-8 h-[450px] overflow-y-auto"
            id="scrollableDiv"
          >
            {isLoading ? (
              <div className="flex h-[102px] justify-center">
                <Loader />
              </div>
            ) : tokens.length > 0 ? (
              <InfiniteScroll
                scrollableTarget="scrollableDiv"
                className="flex flex-col gap-3 px-8 pb-8 pt-1 sm:pr-6"
                dataLength={tokens.length}
                next={() =>
                  getTokens({
                    page,
                    searchValue: debounsedSearchValue,
                    networkId: currentNetwork?.id,
                  })
                }
                hasMore={haseMore}
                loader={<span className="col-span-full">Loading...</span>}
                endMessage={null}
                scrollThreshold="100px"
              >
                {tokens.map((tokenItem) => (
                  <TokenElement
                    key={tokenItem.address}
                    token={tokenItem}
                    isAdmin={isAdmin}
                    onSuccess={() =>
                      getTokens({
                        searchValue: debounsedSearchValue,
                        isSetNewTokens: true,
												networkId: currentNetwork?.id,
                      })
                    }
                  />
                ))}
              </InfiniteScroll>
            ) : (
              <Box
                sx={{
                  textAlign: "center",
                  fontWeight: 500,
                }}
              >
                No tokens.
              </Box>
            )}
          </div>

          {page !== 1 && (
            <Button
              className="absolute bottom-4 right-[50px] flex w-[45px] justify-center px-0"
              onClick={() => scrollToTop({ elementId: "scrollableDiv" })}
            >
              <span className="rotate-90">
                <ArrowLeftIcon />
              </span>
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};
